import React, { useMemo } from 'react';
import { AgendaItem } from '@/types';
import { Badge, BadgeTone } from '@/components/ui/Badge';
import { sameDay, groupByDay, eventsInMonth, upcomingEvents } from '@/lib/dateUtils';
import { TONE_DOT, TYPE_LABEL } from './EventPill';

interface Props {
  cursor: Date;
  events: AgendaItem[];
}

const TONE_BADGE: Record<AgendaItem['type'], BadgeTone> = {
  edital:   'primary',
  certidao: 'warning',
  contrato: 'success',
  sessao:   'info',
  tarefa:   'neutral',
};

export function TimelineView({ cursor, events }: Props) {
  const monthEvents = useMemo(() => eventsInMonth(events, cursor), [events, cursor]);
  const byDay = useMemo(() => groupByDay(monthEvents), [monthEvents]);
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1)),
    [cursor, daysInMonth]
  );
  const maxCount = Math.max(1, ...days.map(d => (byDay[d.toDateString()] ?? []).length));
  const today = new Date();

  const next = useMemo(() => upcomingEvents(events, 10), [events]);

  return (
    <div className="flex flex-col gap-6">
      {/* Heatmap */}
      <section>
        <header className="flex items-center justify-between mb-3">
          <h3 className="text-[14px] font-semibold text-ink-title dark:text-ink-title-dark">
            Densidade de compromissos
          </h3>
          <div className="flex items-center gap-2 text-[10.5px] text-ink-subtitle dark:text-ink-subtitle-dark">
            <span>menos</span>
            <div className="flex gap-[2px]">
              {[0.2, 0.4, 0.6, 0.8, 1].map(a => (
                <div
                  key={a}
                  className="w-3 h-3 rounded-sm"
                  style={{ background: `color-mix(in oklab, var(--color-brand-500) ${a * 100}%, transparent)` }}
                />
              ))}
            </div>
            <span>mais</span>
          </div>
        </header>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(28px,1fr))] gap-[3px]">
          {days.map((day, i) => {
            const count = (byDay[day.toDateString()] ?? []).length;
            const alpha = count === 0 ? 0 : 0.20 + (count / maxCount) * 0.80;
            const isToday = sameDay(day, today);
            const baseClasses = [
              'aspect-square rounded flex items-center justify-center text-[9px] font-medium tabular-nums transition-transform hover:scale-[1.15]',
              count === 0 ? 'bg-surface-alt dark:bg-surface-dark-alt text-ink-label dark:text-ink-label-dark' : '',
              isToday ? 'ring-2 ring-brand-500' : '',
            ].join(' ');
            const style: React.CSSProperties = count > 0
              ? {
                  background: `color-mix(in oklab, var(--color-brand-500) ${Math.round(alpha * 100)}%, transparent)`,
                  color: alpha > 0.55 ? '#fff' : undefined,
                  animation: `scale-in 260ms ease ${i * 6}ms both`,
                }
              : { animationDelay: `${i * 6}ms` };
            return (
              <div key={i} className={baseClasses} style={style} title={`${day.toLocaleDateString('pt-BR')} · ${count} compromisso${count === 1 ? '' : 's'}`}>
                {day.getDate()}
              </div>
            );
          })}
        </div>
      </section>

      {/* Próximos 10 */}
      <section>
        <header className="mb-3">
          <h3 className="text-[14px] font-semibold text-ink-title dark:text-ink-title-dark">
            Próximos compromissos
          </h3>
          <p className="text-[12px] text-ink-subtitle dark:text-ink-subtitle-dark">
            Timeline vertical a partir de hoje
          </p>
        </header>

        {next.length === 0 ? (
          <div className="py-10 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
            Nenhum compromisso futuro.
          </div>
        ) : (
          <ul className="relative pl-8 before:absolute before:left-[14px] before:top-0 before:bottom-0 before:w-px before:bg-line dark:before:bg-line-dark">
            {next.map(e => {
              const d = new Date(e.date);
              const day = String(d.getDate()).padStart(2, '0');
              const month = d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase();
              const hour = String(e.hour ?? 9).padStart(2, '0');
              return (
                <li key={e.id} className="relative pb-5 last:pb-0">
                  <span className={`absolute -left-[22px] top-1 w-2.5 h-2.5 rounded-full ring-4 ring-bg dark:ring-bg-dark ${TONE_DOT[e.type]}`} />
                  <div className="flex items-start gap-4">
                    <div className="w-[72px] flex-shrink-0 text-right">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">
                        {month}
                      </div>
                      <div className="text-[22px] font-extralight tabular-nums text-ink-title dark:text-ink-title-dark leading-none">
                        {day}
                      </div>
                      <div className="text-[10.5px] text-ink-subtitle dark:text-ink-subtitle-dark tabular-nums mt-0.5">
                        {hour}h
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Badge tone={TONE_BADGE[e.type]}>{TYPE_LABEL[e.type]}</Badge>
                      </div>
                      <div className="text-[13.5px] font-medium text-ink-title dark:text-ink-title-dark truncate">
                        {e.title}
                      </div>
                      {e.subtitle && (
                        <div className="text-[11.5px] text-ink-subtitle dark:text-ink-subtitle-dark truncate">
                          {e.subtitle}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
